import React from 'react';

/**
 * Normalize a string for comparison (lowercase, trim whitespace)
 */
function normalizeForComparison(str) {
  return str?.toLowerCase().trim() || '';
}

/**
 * Find item index by matching bold text against headlines.
 * Supports both exact matches and prefix matches (for shortened headlines).
 * For prefix matches, prefers the shortest matching headline (most specific match).
 *
 * @param {string} boldText - The bold text from AI output
 * @param {Array} items - Array of items with headlines
 * @returns {number} - Index of matching item, or -1 if no match
 */
function findItemIndexByHeadline(boldText, items) {
  const normalizedBold = normalizeForComparison(boldText);
  if (!normalizedBold) return -1;

  // Minimum length to avoid false positives with very short matches
  const MIN_MATCH_LENGTH = 10;

  // First try exact match (highest priority)
  for (let i = 0; i < items.length; i++) {
    const headline = items[i].headline || items[i].title || '';
    if (normalizeForComparison(headline) === normalizedBold) {
      return i;
    }
  }

  // Skip prefix matching for very short bold text to avoid false positives
  if (normalizedBold.length < MIN_MATCH_LENGTH) {
    return -1;
  }

  // Then try prefix match (AI often shortens headlines)
  // e.g., "Vegan Thai Co." matches "Vegan Thai Co. - authentic vegan Thai..."
  // Prefer the shortest matching headline (most specific match)
  let bestMatchIndex = -1;
  let bestMatchLength = Infinity;

  for (let i = 0; i < items.length; i++) {
    const headline = items[i].headline || items[i].title || '';
    const normalizedHeadline = normalizeForComparison(headline);
    if (normalizedHeadline.startsWith(normalizedBold)) {
      // Prefer shorter headlines (more specific match)
      if (normalizedHeadline.length < bestMatchLength) {
        bestMatchIndex = i;
        bestMatchLength = normalizedHeadline.length;
      }
    }
  }

  return bestMatchIndex;
}

/**
 * Transform item references in content to clickable markdown links.
 *
 * Bold headlines are the only reference contract (COL-38 / COL-81): the model
 * is instructed to name an item by bolding its headline, and the link carries
 * that item's id. Positional patterns ("1.", "[1]", "item 1") used to be
 * transformed too, but `items[]` is merged and deduped across every tool call
 * in a turn, so the model's own numbering has no relationship to array
 * position — those links could only ever be plausibly wrong, so they are now
 * left as plain text.
 *
 * @param {string} content - The markdown content to transform
 * @param {Array} items - Array of items to reference (matched by headline)
 * @returns {string} - Transformed content with item links
 */
export function transformItemReferences(content, items) {
  if (!items || items.length === 0) return content;

  // Bold headlines that match item headlines - "**Tokyo Hotel Search**"
  // Match bold text that's NOT already inside a link (not preceded by [ or followed by ])
  // Supports both exact matches and prefix matches (AI often shortens headlines)
  return content.replace(/(?<!\[)\*\*([^*]+)\*\*(?!\]|\()(\s?)/g, (match, title, trailingSpace) => {
    const index = findItemIndexByHeadline(title, items);
    if (index === -1) return match;
    const itemId = items[index].item_id || items[index].id;
    if (!itemId) return match;
    // Ensure a space after the link so text doesn't run together
    return `[**${title}**](#item-${itemId})${trailingSpace || ' '}`;
  });
}

/**
 * Custom link component for ReactMarkdown - handles item links and external links
 * Sanitizes URLs to prevent XSS attacks via javascript: or other dangerous protocols
 * Handles special #item-<uuid> (chat) and #item-<index> (search answer)
 * links to navigate to referenced items
 *
 * @param {Object} props - Link props from ReactMarkdown
 * @param {Function} props.onItemClick - Callback when an item link is clicked
 * @param {Array} props.items - Array of items the #item-* references resolve against
 */
export function ItemLink({ href, children, onItemClick, items, ...props }) {
  const EXTERNAL_PROTOCOLS = ['http:', 'https:'];
  const SAFE_PROTOCOLS = [...EXTERNAL_PROTOCOLS, 'mailto:'];

  // Classify the href up front (COL-325): an external http(s)/mailto URL must
  // never be hijacked by the uuid-in-href or headline-text branches below,
  // which match on content, not on where the link actually points.
  let safeHref = href;
  let isExternal = false;
  let hasExternalScheme = false; // http:, https:, or mailto: — bypasses matching below

  if (href) {
    try {
      const url = new URL(href, window.location.origin);
      if (!SAFE_PROTOCOLS.includes(url.protocol)) {
        safeHref = '#'; // Block dangerous protocols like javascript:, data:, vbscript:
      } else {
        isExternal = EXTERNAL_PROTOCOLS.includes(url.protocol);
      }
    } catch {
      // Invalid URL syntax - could be a relative path, allow it
    }

    // Resolve WITHOUT a base so a bare "#", "#item-…", or "/items/…" (which
    // would otherwise inherit the page's http(s) origin and look external
    // too) only counts here when the href itself carries an explicit scheme.
    try {
      hasExternalScheme = SAFE_PROTOCOLS.includes(new URL(href).protocol);
    } catch {
      hasExternalScheme = false;
    }
  }

  // Positional item links (#item-0, #item-1, ...). transformItemReferences no
  // longer emits these — chat links carry the item's uuid (COL-81) — but the
  // Gallery AI summary still needs them: search_service._generate_answer
  // labels each context item "Item (link: #item-N)" by its position in the
  // same array the frontend receives, and the generate-answer prompt tells
  // the model to link with short descriptive text rather than the headline,
  // so neither the uuid branch nor the headline-text branch below can resolve
  // one. The index is authoritative on that path, not a guess.
  const itemMatch = href?.match(/^#item-(\d+)$/);
  if (itemMatch && items) {
    const itemIndex = parseInt(itemMatch[1], 10);
    const item = items[itemIndex];
    if (item) {
      return (
        <a
          href={href}
          className="chat-inline-item-link"
          onClick={(e) => {
            e.preventDefault();
            onItemClick?.({ item_id: item.item_id || item.id, ...item });
          }}
          title={`View: ${item.headline || item.title || item.summary || 'Item'}`}
        >
          {children}
        </a>
      );
    }
  }

  // Check if href contains an item ID (UUID v4) that matches a known item
  if (items?.length > 0 && href && !hasExternalScheme) {
    const uuidMatch = href.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (uuidMatch) {
      const matchedItem = items.find(
        (it) => (it.item_id || it.id) === uuidMatch[1]
      );
      if (matchedItem) {
        return (
          <a
            href={href}
            className="chat-inline-item-link"
            onClick={(e) => {
              e.preventDefault();
              onItemClick?.({ item_id: matchedItem.item_id || matchedItem.id, ...matchedItem });
            }}
            title={`View: ${matchedItem.headline || matchedItem.title || matchedItem.summary || 'Item'}`}
          >
            {children}
          </a>
        );
      }
    }

    // Check if link text matches an item headline
    const linkText = extractTextFromChildren(children);
    if (linkText) {
      const matchIndex = findItemIndexByHeadline(linkText, items);
      if (matchIndex !== -1) {
        const item = items[matchIndex];
        return (
          <a
            href={href}
            className="chat-inline-item-link"
            onClick={(e) => {
              e.preventDefault();
              onItemClick?.({ item_id: item.item_id || item.id, ...item });
            }}
            title={`View: ${item.headline || item.title || item.summary || 'Item'}`}
          >
            {children}
          </a>
        );
      }
    }
  }

  // Catch /items/UUID paths (relative or absolute API URL) and open modal
  if (href && onItemClick) {
    const itemPathMatch = href.match(/\/items\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (itemPathMatch) {
      return (
        <a
          href={href}
          className="chat-inline-item-link"
          onClick={(e) => {
            e.preventDefault();
            onItemClick({ item_id: itemPathMatch[1] });
          }}
          title="View item"
        >
          {children}
        </a>
      );
    }
  }

  return (
    <a
      href={safeHref}
      {...props}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

/**
 * Extract plain text from React children (handles nested elements like <strong>).
 */
function extractTextFromChildren(children) {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (children?.props?.children) return extractTextFromChildren(children.props.children);
  return '';
}
